import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import type { Property, PropertyCreateData, PropertyUpdateData } from '@/lib/api'
import type { StagedImage } from '@/hooks'
import { useBuildings, usePropertyDocuments } from '@/hooks'
import { ImageUploader } from './ImageUploader'
import { ImageGallery } from './ImageGallery'
import { StagedImageUploader } from './StagedImageUploader'
import { DocumentUploader } from './DocumentUploader'
import { DocumentGallery } from './DocumentGallery'

// ============================================================================
// Form Schema
// ============================================================================

const propertyFormSchema = z.object({
  title: z
    .string()
    .min(1, 'El título es obligatorio')
    .max(255, 'El título debe tener 255 caracteres o menos'),
  slug: z
    .string()
    .min(1, 'El slug es obligatorio')
    .max(255, 'El slug debe tener 255 caracteres o menos')
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      'El slug debe ser en minúsculas solo con guiones (ej., mi-nombre-de-propiedad)'
    ),
  status: z.enum(['draft', 'published', 'archived']),
  description_short: z.string().max(500, 'La descripción corta debe tener 500 caracteres o menos').optional().or(z.literal('')),
  description_long: z.string().max(10000, 'La descripción larga debe tener 10,000 caracteres o menos').optional().or(z.literal('')),
  // Especificaciones (anidadas)
  bedrooms: z.coerce.number().min(0, 'Debe ser 0 o más').optional().or(z.literal('')),
  bathrooms: z.coerce.number().min(0, 'Debe ser 0 o más').optional().or(z.literal('')),
  area_m2: z.coerce.number().positive('Debe ser positivo').optional().or(z.literal('')),
  year_built: z.coerce.number().min(1800, 'Debe ser 1800 o posterior').max(2030, 'Debe ser 2030 o anterior').optional().or(z.literal('')),
  floor: z.string().max(20, 'La planta debe tener 20 caracteres o menos').optional().or(z.literal('')),
  // Ubicación
  address: z.string().max(255, 'La dirección debe tener 255 caracteres o menos').optional().or(z.literal('')),
  city: z.string().max(100, 'La ciudad debe tener 100 caracteres o menos').optional().or(z.literal('')),
  province: z.string().max(100, 'La provincia debe tener 100 caracteres o menos').optional().or(z.literal('')),
  region: z.string().max(100, 'La zona debe tener 100 caracteres o menos').optional().or(z.literal('')),
  postal_code: z.string().max(20, 'El código postal debe tener 20 caracteres o menos').optional().or(z.literal('')),
  // Precio
  price: z.coerce.number().min(0, 'Debe ser 0 o más').optional().or(z.literal('')),
  price_currency: z.enum(['EUR', 'USD', 'GBP']),
  // Opciones
  featured: z.boolean(),
  badge: z.string().max(100, 'La etiqueta debe tener 100 caracteres o menos').optional().or(z.literal('')),
  // Características
  pool: z.boolean(),
  garden: z.boolean(),
  terrace: z.boolean(),
  patio: z.boolean(),
  air_conditioning: z.boolean(),
  elevator: z.boolean(),
  garage: z.boolean(),
  // Características destacadas (para mostrar con énfasis en la página de detalles)
  highlighted_features: z.array(z.string()),
  // Building assignment - for properties that belong to a development/promotion
  building_id: z.string().optional().or(z.literal('')),
  // Building-specific fields (only used when building_id is set)
  reference_code: z.string().max(50, 'El código de referencia debe tener 50 caracteres o menos').optional().or(z.literal('')),
  storage_room: z.boolean(), // Trastero - stored in characteristics
  balcony: z.boolean(), // Balcón
  exterior: z.boolean(), // Exterior
  jacuzzi: z.boolean(), // Jacuzzi
  sauna: z.boolean(), // Sauna
  bbq_area: z.boolean(), // Zona de barbacoa
  sea_views: z.boolean(), // Vistas al mar
  floor_plan_id: z.string().optional().or(z.literal('')),
  // When true, property is ONLY shown in building's table, not in property listings
  building_only: z.boolean(),
})

type PropertyFormData = z.infer<typeof propertyFormSchema>

// ============================================================================
// Slug Generation Utility
// ============================================================================

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-|-$/g, '') // Remove leading/trailing hyphens
}

// ============================================================================
// Highlighted Features Selector Component
// ============================================================================

interface FeatureOption {
  key: string
  label: string
  formField: keyof PropertyFormData
}

const FEATURE_OPTIONS: FeatureOption[] = [
  { key: 'pool', label: 'Piscina', formField: 'pool' },
  { key: 'garden', label: 'Jardín', formField: 'garden' },
  { key: 'terrace', label: 'Terraza', formField: 'terrace' },
  { key: 'patio', label: 'Patio', formField: 'patio' },
  { key: 'balcony', label: 'Balcón', formField: 'balcony' },
  { key: 'air_conditioning', label: 'Aire acondicionado', formField: 'air_conditioning' },
  { key: 'elevator', label: 'Ascensor', formField: 'elevator' },
  { key: 'garage', label: 'Garaje', formField: 'garage' },
  { key: 'storage_room', label: 'Trastero', formField: 'storage_room' },
  { key: 'exterior', label: 'Exterior', formField: 'exterior' },
  { key: 'jacuzzi', label: 'Jacuzzi', formField: 'jacuzzi' },
  { key: 'sauna', label: 'Sauna', formField: 'sauna' },
  { key: 'bbq_area', label: 'Zona de barbacoa', formField: 'bbq_area' },
  { key: 'sea_views', label: 'Vistas al mar', formField: 'sea_views' },
]

interface HighlightedFeaturesSelectorProps {
  watch: ReturnType<typeof useForm<PropertyFormData>>['watch']
  setValue: ReturnType<typeof useForm<PropertyFormData>>['setValue']
  isDisabled: boolean
}

function HighlightedFeaturesSelector({ watch, setValue, isDisabled }: HighlightedFeaturesSelectorProps) {
  const highlightedFeatures = watch('highlighted_features') || []

  // Get enabled features to show as highlightable options
  const enabledFeatures = FEATURE_OPTIONS.filter(option => watch(option.formField))

  if (enabledFeatures.length === 0) {
    return null
  }

  const toggleHighlight = (featureKey: string) => {
    const current = highlightedFeatures || []
    const updated = current.includes(featureKey)
      ? current.filter(k => k !== featureKey)
      : [...current, featureKey]
    setValue('highlighted_features', updated, { shouldDirty: true })
  }

  return (
    <div className="mt-6 border-t pt-4">
      <h3 className="mb-2 text-sm font-medium text-muted-foreground">
        Características Destacadas
      </h3>
      <p className="mb-3 text-xs text-muted-foreground">
        Selecciona las características que quieres resaltar en la página de detalles de la propiedad.
      </p>
      <div className="flex flex-wrap gap-2">
        {enabledFeatures.map(option => {
          const isHighlighted = highlightedFeatures.includes(option.key)
          return (
            <button
              key={option.key}
              type="button"
              disabled={isDisabled}
              onClick={() => toggleHighlight(option.key)}
              className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                isHighlighted
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-input bg-background hover:bg-accent'
              }`}
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ============================================================================
// Form Component
// ============================================================================

interface PropertyFormPropsBase {
  /** Existing property data for edit mode */
  property?: Property
  /** Delete handler (edit mode only) */
  onDelete?: () => void
  /** Whether form is submitting */
  isSubmitting?: boolean
  /** Whether delete is in progress */
  isDeleting?: boolean
  /** Submission error message */
  submitError?: string | null
}

interface PropertyFormCreateProps extends PropertyFormPropsBase {
  /** Whether form is in edit mode */
  isEdit?: false
  /** Submit handler for create - includes staged images */
  onSubmit: (data: PropertyCreateData, stagedImages: StagedImage[]) => void
}

interface PropertyFormEditProps extends PropertyFormPropsBase {
  /** Whether form is in edit mode */
  isEdit: true
  /** Submit handler for update */
  onSubmit: (data: PropertyUpdateData) => void
}

type PropertyFormProps = PropertyFormCreateProps | PropertyFormEditProps

export function PropertyForm({
  property,
  isEdit = false,
  onSubmit,
  onDelete,
  isSubmitting = false,
  isDeleting = false,
  submitError,
}: PropertyFormProps) {
  // Staged images state for create mode
  const [stagedImages, setStagedImages] = useState<StagedImage[]>([])

  // Fetch buildings for the dropdown
  const { data: buildings, isLoading: buildingsLoading } = useBuildings()

  // Fetch documents for floor plan selector (only in edit mode)
  const propertyId = isEdit ? (property as any)?._id : undefined
  const { data: documents, isLoading: documentsLoading } = usePropertyDocuments(propertyId)

  const defaultValues: PropertyFormData = useMemo(() => ({
    title: property?.title ?? '',
    slug: property?.slug ?? '',
    status: property?.status ?? 'draft',
    description_short: property?.description_short ?? '',
    description_long: property?.description_long ?? '',
    bedrooms: property?.specs?.bedrooms ?? '',
    bathrooms: property?.specs?.bathrooms ?? '',
    area_m2: property?.specs?.area_m2 ?? '',
    year_built: property?.specs?.year_built ?? '',
    floor: property?.specs?.floor ?? '',
    address: property?.address ?? '',
    city: property?.city ?? '',
    province: property?.province ?? '',
    region: property?.region ?? '',
    postal_code: property?.postal_code ?? '',
    price: property?.price ?? '',
    price_currency: property?.price_currency as 'EUR' | 'USD' | 'GBP' ?? 'EUR',
    featured: property?.featured ?? false,
    badge: property?.badge ?? '',
    // Características
    pool: property?.characteristics?.pool ?? false,
    garden: property?.characteristics?.garden ?? false,
    terrace: property?.characteristics?.terrace ?? false,
    patio: property?.characteristics?.patio ?? false,
    air_conditioning: property?.characteristics?.air_conditioning ?? false,
    elevator: property?.characteristics?.elevator ?? false,
    garage: property?.characteristics?.garage ?? property?.specs?.garage ?? false, // Check both for backward compatibility
    // Características destacadas
    highlighted_features: property?.characteristics?.highlighted_features ?? [],
    // Building assignment
    building_id: (property as any)?.building_id ?? '',
    reference_code: (property as any)?.reference_code ?? '',
    storage_room: property?.characteristics?.storage_room ?? false,
    balcony: property?.characteristics?.balcony ?? false,
    exterior: property?.characteristics?.exterior ?? false,
    jacuzzi: property?.characteristics?.jacuzzi ?? false,
    sauna: property?.characteristics?.sauna ?? false,
    bbq_area: property?.characteristics?.bbq_area ?? false,
    sea_views: property?.characteristics?.sea_views ?? false,
    floor_plan_id: (property as any)?.floor_plan_id ?? '',
    building_only: (property as any)?.building_only ?? false,
  }), [property])

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<PropertyFormData>({
    resolver: zodResolver(propertyFormSchema),
    defaultValues,
  })

  // Track if user has manually edited the slug field
  const slugManuallyEdited = useRef(false)
  const lastAutoSlug = useRef('')

  // Reset form when property data changes to sync dirty tracking baseline
  useEffect(() => {
    reset(defaultValues)
    // Reset slug tracking on form reset
    slugManuallyEdited.current = false
    lastAutoSlug.current = ''
  }, [defaultValues, reset])

  // Explicitly set building-related fields when both property and buildings are loaded
  // This ensures the select shows the correct value even with async option loading
  useEffect(() => {
    if (property && buildings && !buildingsLoading) {
      const buildingId = (property as any)?.building_id
      if (buildingId) {
        // Verify the building exists in the options before setting
        const buildingExists = buildings.some(b => b._id === buildingId)
        if (buildingExists) {
          setValue('building_id', buildingId, { shouldDirty: false })
          // Also set related fields
          const refCode = (property as any)?.reference_code
          if (refCode) {
            setValue('reference_code', refCode, { shouldDirty: false })
          }
          const buildingOnly = (property as any)?.building_only
          if (buildingOnly !== undefined) {
            setValue('building_only', buildingOnly, { shouldDirty: false })
          }
        }
      }
    }
  }, [property, buildings, buildingsLoading, setValue])

  // Explicitly set floor_plan_id when both property and documents are loaded
  useEffect(() => {
    if (property && documents && !documentsLoading) {
      const floorPlanId = (property as any)?.floor_plan_id
      if (floorPlanId) {
        // Verify the document exists in the options before setting
        const documentExists = documents.some(d => d.id === floorPlanId)
        if (documentExists) {
          setValue('floor_plan_id', floorPlanId, { shouldDirty: false })
        }
      }
    }
  }, [property, documents, documentsLoading, setValue])

  // Watch title for auto-slug generation
  const title = watch('title')

  // Watch building_id to conditionally show building-specific fields
  const selectedBuildingId = watch('building_id')

  // Auto-generate slug from title (only in create mode and if slug hasn't been manually edited)
  useEffect(() => {
    if (!isEdit && title && !slugManuallyEdited.current) {
      const generatedSlug = generateSlug(title)
      lastAutoSlug.current = generatedSlug
      setValue('slug', generatedSlug, { shouldValidate: true })
    }
  }, [title, isEdit, setValue])

  // Handle manual slug edits
  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    // If user types something different from what auto-generation would produce, mark as manually edited
    if (newValue !== lastAutoSlug.current) {
      slugManuallyEdited.current = true
    }
    // If user clears the field, re-enable auto-generation
    if (newValue === '') {
      slugManuallyEdited.current = false
    }
  }

  // Transform form data to API format
  function transformFormData(data: PropertyFormData): PropertyCreateData {
    const specs: Record<string, number | string | undefined> = {}
    if (data.bedrooms !== '' && data.bedrooms !== undefined) specs.bedrooms = Number(data.bedrooms)
    if (data.bathrooms !== '' && data.bathrooms !== undefined) specs.bathrooms = Number(data.bathrooms)
    if (data.area_m2 !== '' && data.area_m2 !== undefined) specs.area_m2 = Number(data.area_m2)
    if (data.year_built !== '' && data.year_built !== undefined) specs.year_built = Number(data.year_built)
    if (data.floor !== '' && data.floor !== undefined) specs.floor = data.floor

    // Build characteristics object - always include all values to ensure proper updates
    const characteristics = {
      pool: data.pool || false,
      garden: data.garden || false,
      terrace: data.terrace || false,
      patio: data.patio || false,
      balcony: data.balcony || false,
      air_conditioning: data.air_conditioning || false,
      elevator: data.elevator || false,
      garage: data.garage || false,
      storage_room: data.storage_room || false,
      exterior: data.exterior || false,
      jacuzzi: data.jacuzzi || false,
      sauna: data.sauna || false,
      bbq_area: data.bbq_area || false,
      sea_views: data.sea_views || false,
      highlighted_features: data.highlighted_features || [],
    }

    // Build the result with building fields if a building is selected
    const result: PropertyCreateData & {
      building_id?: string
      reference_code?: string
      floor_plan_id?: string
      building_only?: boolean
    } = {
      title: data.title,
      slug: data.slug,
      status: data.status,
      description_short: data.description_short || null,
      description_long: data.description_long || null,
      specs: Object.keys(specs).length > 0 ? specs : undefined,
      characteristics,
      address: data.address || null,
      city: data.city || null,
      province: data.province || null,
      region: data.region || null,
      postal_code: data.postal_code || null,
      price: data.price !== '' && data.price !== undefined ? Number(data.price) : null,
      price_currency: data.price_currency,
      featured: data.featured,
      badge: data.badge || undefined,
    }

    // Add building fields if a building is selected
    if (data.building_id && data.building_id !== '') {
      result.building_id = data.building_id
      if (data.reference_code && data.reference_code !== '') {
        result.reference_code = data.reference_code
      }
      // Always include floor_plan_id when building is selected (even if empty to clear it)
      result.floor_plan_id = data.floor_plan_id && data.floor_plan_id !== '' ? data.floor_plan_id : undefined
      result.building_only = data.building_only
    } else {
      // Clear building-related fields when no building is selected
      result.building_id = undefined
      result.reference_code = undefined
      result.floor_plan_id = undefined
      result.building_only = undefined
    }

    return result
  }

  function handleFormSubmit(data: PropertyFormData) {
    // The form data always has required fields (title, slug) due to Zod validation
    // For create mode, we pass PropertyCreateData; for edit mode, PropertyUpdateData
    // Since PropertyUpdateData is Partial<PropertyCreateData>, the full data works for both
    const transformedData = transformFormData(data)

    if (isEdit) {
      // Edit mode - just pass the data
      ;(onSubmit as (data: PropertyUpdateData) => void)(transformedData)
    } else {
      // Create mode - pass data and staged images
      ;(onSubmit as (data: PropertyCreateData, stagedImages: StagedImage[]) => void)(transformedData, stagedImages)
    }
  }

  const isDisabled = isSubmitting || isDeleting

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-8">
      {/* Alerta de Error */}
      {submitError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <strong>Error:</strong> {submitError}
        </div>
      )}

      {/* Información Básica */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Información Básica</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label htmlFor="title" className="block text-sm font-medium">
              Título <span className="text-red-500">*</span>
            </label>
            <input
              id="title"
              type="text"
              placeholder="Hermosa Villa en Costa Blanca"
              disabled={isDisabled}
              {...register('title')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.title && (
              <p className="mt-1 text-xs text-red-500">{errors.title.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="slug" className="block text-sm font-medium">
              Slug <span className="text-red-500">*</span>
            </label>
            <input
              id="slug"
              type="text"
              placeholder="hermosa-villa-costa-blanca"
              disabled={isDisabled}
              {...register('slug', {
                onChange: handleSlugChange,
              })}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Identificador amigable para URL (generado automáticamente del título)
            </p>
            {errors.slug && (
              <p className="mt-1 text-xs text-red-500">{errors.slug.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="status" className="block text-sm font-medium">
              Estado
            </label>
            <select
              id="status"
              disabled={isDisabled}
              {...register('status')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="draft">Borrador</option>
              <option value="published">Publicado</option>
              <option value="archived">Archivado</option>
            </select>
            {errors.status && (
              <p className="mt-1 text-xs text-red-500">{errors.status.message}</p>
            )}
          </div>
          <div className="md:col-span-2">
            <label htmlFor="badge" className="block text-sm font-medium">
              Etiqueta
            </label>
            <input
              id="badge"
              type="text"
              placeholder="Segunda Mano | Oportunidad"
              disabled={isDisabled}
              {...register('badge')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Etiqueta opcional para mostrar en tarjetas (ej., "Segunda Mano", "Oportunidad")
            </p>
            {errors.badge && (
              <p className="mt-1 text-xs text-red-500">{errors.badge.message}</p>
            )}
          </div>
        </div>
      </section>

      {/* Edificio (Building Assignment) */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Edificio</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Asigna esta propiedad a un edificio/promoción para mostrarla en la tabla de tipologías.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label htmlFor="building_id" className="block text-sm font-medium">
              Edificio / Promoción
            </label>
            <select
              id="building_id"
              disabled={isDisabled || buildingsLoading}
              {...register('building_id')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">Sin edificio asignado</option>
              {buildings?.map(building => (
                <option key={building._id} value={building._id}>
                  {building.name} {building.city ? `(${building.city})` : ''}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">
              Selecciona un edificio si esta propiedad pertenece a una promoción inmobiliaria.
            </p>
            {errors.building_id && (
              <p className="mt-1 text-xs text-red-500">{errors.building_id.message}</p>
            )}
          </div>

          {/* Building-specific fields - only shown when a building is selected */}
          {selectedBuildingId && selectedBuildingId !== '' && (
            <>
              <div>
                <label htmlFor="reference_code" className="block text-sm font-medium">
                  Código de Referencia
                </label>
                <input
                  id="reference_code"
                  type="text"
                  placeholder="VI-3-P02-E"
                  disabled={isDisabled}
                  {...register('reference_code')}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono disabled:cursor-not-allowed disabled:opacity-50"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Código interno de la vivienda (ej: VI-3-P02-E)
                </p>
                {errors.reference_code && (
                  <p className="mt-1 text-xs text-red-500">{errors.reference_code.message}</p>
                )}
              </div>

              {/* Floor plan selector - only shown in edit mode when documents are available */}
              {isEdit && (
                <div>
                  <label htmlFor="floor_plan_id" className="block text-sm font-medium">
                    Plano de la vivienda
                  </label>
                  <select
                    id="floor_plan_id"
                    disabled={isDisabled || documentsLoading}
                    {...register('floor_plan_id')}
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Sin plano asignado</option>
                    {documents?.map(doc => (
                      <option key={doc.id} value={doc.id}>
                        {doc.name} ({doc.file_type.toUpperCase()})
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Selecciona un documento como plano de la vivienda. Primero sube el documento en la sección de Documentos.
                  </p>
                  {errors.floor_plan_id && (
                    <p className="mt-1 text-xs text-red-500">{errors.floor_plan_id.message}</p>
                  )}
                </div>
              )}

              {/* Building-only visibility option */}
              <div className="md:col-span-2 mt-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    disabled={isDisabled}
                    {...register('building_only')}
                    className="mt-0.5 h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <div>
                    <span className="text-sm font-medium">Solo mostrar en tabla del edificio</span>
                    <p className="text-xs text-muted-foreground">
                      Si está marcado, esta propiedad solo aparecerá en la tabla de tipologías del edificio, no en el listado general de propiedades.
                    </p>
                  </div>
                </label>
                {errors.building_only && (
                  <p className="mt-1 text-xs text-red-500">{errors.building_only.message}</p>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Descripciones */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Descripciones</h2>
        <div className="space-y-4">
          <div>
            <label htmlFor="description_short" className="block text-sm font-medium">
              Descripción Corta
            </label>
            <textarea
              id="description_short"
              rows={2}
              placeholder="Un breve resumen de la propiedad..."
              disabled={isDisabled}
              {...register('description_short')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.description_short && (
              <p className="mt-1 text-xs text-red-500">{errors.description_short.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="description_long" className="block text-sm font-medium">
              Descripción Larga
            </label>
            <textarea
              id="description_long"
              rows={6}
              placeholder="Descripción detallada de la propiedad..."
              disabled={isDisabled}
              {...register('description_long')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.description_long && (
              <p className="mt-1 text-xs text-red-500">{errors.description_long.message}</p>
            )}
          </div>
        </div>
      </section>

      {/* Especificaciones */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Especificaciones</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <label htmlFor="bedrooms" className="block text-sm font-medium">
              Habitaciones
            </label>
            <input
              id="bedrooms"
              type="number"
              min="0"
              placeholder="3"
              disabled={isDisabled}
              {...register('bedrooms')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.bedrooms && (
              <p className="mt-1 text-xs text-red-500">{errors.bedrooms.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="bathrooms" className="block text-sm font-medium">
              Baños
            </label>
            <input
              id="bathrooms"
              type="number"
              min="0"
              step="0.5"
              placeholder="2"
              disabled={isDisabled}
              {...register('bathrooms')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.bathrooms && (
              <p className="mt-1 text-xs text-red-500">{errors.bathrooms.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="area_m2" className="block text-sm font-medium">
              Superficie (m²)
            </label>
            <input
              id="area_m2"
              type="number"
              min="0"
              placeholder="150"
              disabled={isDisabled}
              {...register('area_m2')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.area_m2 && (
              <p className="mt-1 text-xs text-red-500">{errors.area_m2.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="year_built" className="block text-sm font-medium">
              Año de Construcción
            </label>
            <input
              id="year_built"
              type="number"
              min="1800"
              max="2030"
              placeholder="2020"
              disabled={isDisabled}
              {...register('year_built')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.year_built && (
              <p className="mt-1 text-xs text-red-500">{errors.year_built.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="floor" className="block text-sm font-medium">
              Planta
            </label>
            <input
              id="floor"
              type="text"
              placeholder="baja, ático, 4"
              disabled={isDisabled}
              {...register('floor')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Nivel de planta (ej: baja, ático, 4)
            </p>
            {errors.floor && (
              <p className="mt-1 text-xs text-red-500">{errors.floor.message}</p>
            )}
          </div>
        </div>
      </section>

      {/* Ubicación */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Ubicación</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <label htmlFor="address" className="block text-sm font-medium">
              Dirección
            </label>
            <input
              id="address"
              type="text"
              placeholder="Calle Principal 123"
              disabled={isDisabled}
              {...register('address')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.address && (
              <p className="mt-1 text-xs text-red-500">{errors.address.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="city" className="block text-sm font-medium">
              Ciudad
            </label>
            <input
              id="city"
              type="text"
              placeholder="Alicante"
              disabled={isDisabled}
              {...register('city')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.city && (
              <p className="mt-1 text-xs text-red-500">{errors.city.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="province" className="block text-sm font-medium">
              Provincia
            </label>
            <input
              id="province"
              type="text"
              placeholder="Alicante"
              disabled={isDisabled}
              {...register('province')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.province && (
              <p className="mt-1 text-xs text-red-500">{errors.province.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="region" className="block text-sm font-medium">
              Zona
            </label>
            <input
              id="region"
              type="text"
              placeholder="Costa Blanca"
              disabled={isDisabled}
              {...register('region')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.region && (
              <p className="mt-1 text-xs text-red-500">{errors.region.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="postal_code" className="block text-sm font-medium">
              Código Postal
            </label>
            <input
              id="postal_code"
              type="text"
              placeholder="03001"
              disabled={isDisabled}
              {...register('postal_code')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.postal_code && (
              <p className="mt-1 text-xs text-red-500">{errors.postal_code.message}</p>
            )}
          </div>
        </div>
      </section>

      {/* Precio */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Precio</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label htmlFor="price" className="block text-sm font-medium">
              Precio
            </label>
            <input
              id="price"
              type="number"
              min="0"
              placeholder="350000"
              disabled={isDisabled}
              {...register('price')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            />
            {errors.price && (
              <p className="mt-1 text-xs text-red-500">{errors.price.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="price_currency" className="block text-sm font-medium">
              Moneda
            </label>
            <select
              id="price_currency"
              disabled={isDisabled}
              {...register('price_currency')}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="EUR">EUR (€)</option>
              <option value="USD">USD ($)</option>
              <option value="GBP">GBP (£)</option>
            </select>
            {errors.price_currency && (
              <p className="mt-1 text-xs text-red-500">{errors.price_currency.message}</p>
            )}
          </div>
        </div>
      </section>

      {/* Opciones */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Opciones</h2>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            disabled={isDisabled}
            {...register('featured')}
            className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
          />
          <span className="text-sm">Propiedad destacada (mostrada en la página principal)</span>
        </label>
        {errors.featured && (
          <p className="mt-1 text-xs text-red-500">{errors.featured.message}</p>
        )}
      </section>

      {/* Características */}
      <section className="rounded-lg border bg-card p-6">
        <h2 className="mb-4 text-lg font-semibold">Características</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('pool')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Piscina</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('garden')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Jardín</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('terrace')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Terraza</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('patio')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Patio</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('air_conditioning')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Aire acondicionado</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('elevator')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Ascensor</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('garage')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Garaje</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('storage_room')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Trastero</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('balcony')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Balcón</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('exterior')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Exterior</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('jacuzzi')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Jacuzzi</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('sauna')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Sauna</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('bbq_area')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Zona de barbacoa</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              disabled={isDisabled}
              {...register('sea_views')}
              className="h-4 w-4 rounded border-input disabled:cursor-not-allowed disabled:opacity-50"
            />
            <span className="text-sm">Vistas al mar</span>
          </label>
        </div>

        {/* Características Destacadas */}
        <HighlightedFeaturesSelector
          watch={watch}
          setValue={setValue}
          isDisabled={isDisabled}
        />
      </section>

      {/* Imágenes */}
      {isEdit && (property as any)?._id ? (
        <div className="space-y-6">
          <ImageUploader propertyId={(property as any)._id} disabled={isDisabled} />
          <ImageGallery propertyId={(property as any)._id} disabled={isDisabled} />
        </div>
      ) : (
        <StagedImageUploader
          images={stagedImages}
          onImagesChange={setStagedImages}
          disabled={isDisabled}
        />
      )}

      {/* Documentos - only shown in edit mode since documents require property_id */}
      {isEdit && (property as any)?._id && (
        <div className="space-y-6">
          <DocumentUploader propertyId={(property as any)._id} disabled={isDisabled} />
          <DocumentGallery propertyId={(property as any)._id} disabled={isDisabled} />
        </div>
      )}

      {/* Acciones */}
      <div className={`flex items-center ${isEdit ? 'justify-between' : 'justify-end'}`}>
        {isEdit && onDelete && (
          <button
            type="button"
            onClick={() => {
              if (confirm('¿Estás seguro de que quieres eliminar esta propiedad? Esta acción no se puede deshacer.')) {
                onDelete()
              }
            }}
            disabled={isDisabled}
            className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
          >
            {isDeleting && <Loader2 className="h-4 w-4 animate-spin" />}
            Eliminar Propiedad
          </button>
        )}
        <div className="flex items-center gap-4">
          <Link
            to="/properties"
            className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Cancelar
          </Link>
          <button
            type="submit"
            disabled={isDisabled}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? 'Guardar Cambios' : 'Crear Propiedad'}
          </button>
        </div>
      </div>
    </form>
  )
}
